import { useState, useEffect } from "react";
import { FaVideo } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import { GetCallHistory } from "../services/api";
import PrivateCallModal from "../components/PrivateCallModal";
import { formatDate, formatDuration, capitalizeFirstLetter, getMediaUrl } from "../utils/helpers";
import { useNavigate } from "react-router-dom";
import { getProfileImage } from "../utils/helpers";

const LIMIT = 10;

export default function History() {
  const [activeTab, setActiveTab] = useState("all");
  const [callHistory, setCallHistory] = useState([]);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [privateCall, setPrivateCall] = useState(false);
  const [hostCallData, setHostCallData] = useState(null);

  const navigate = useNavigate();

  /* ---------------- FETCH HISTORY ---------------- */
  const fetchHistory = async (type, pageNo, reset = false) => {
    if (loading || (!hasMore && !reset)) return;

    try {
      setLoading(true);

      const res = await GetCallHistory(type, pageNo, LIMIT);
      const newData = res.data?.data || [];

      setCallHistory(prev =>
        reset ? newData : [...prev, ...newData]
      );

      setHasMore(newData.length === LIMIT);
      setPage(pageNo);
      setActiveTab(type);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- INITIAL LOAD ---------------- */
  useEffect(() => {
    fetchHistory("all", 1, true);
  }, []);

  /* ---------------- INFINITE SCROLL ---------------- */
  useEffect(() => {
    const handleScroll = () => {
      const reachedBottom =
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 200;

      if (reachedBottom && hasMore && !loading) {
        fetchHistory(activeTab, page + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, activeTab, hasMore, loading]);

  /* ---------------- TAB HANDLERS ---------------- */
  const OnAllClicked = () => {
    if (activeTab === "all") return;
    setHasMore(true);
    fetchHistory("all", 1, true);
  };

  const OnMissedClicked = () => {
    if (activeTab === "missed") return;
    setHasMore(true);
    fetchHistory("missed", 1, true);
  };

  /* ---------------- ITEM HANDLERS ---------------- */
  const OnItemClick = (item) => {
    const userId =
      item.type === "outgoing"
        ? item.receiver?._id
        : item.caller?._id;

    navigate(`/user/${userId}`);
  };

  const OnVideoCallClick = (e, item) => {
    e.stopPropagation();
    setHostCallData(
      item.type === "outgoing" ? item.receiver : item.caller
    );
    setPrivateCall(true);
  };

  return (
    <div className="min-h-screen bg-black text-white px-4 md:px-24 pt-[50px] pb-20">

      {/* ---------------- TABS ---------------- */}
      <div className="flex justify-center gap-5 mb-6 mt-12">
        <button
          onClick={OnAllClicked}
          className={`px-12 py-2 rounded-full font-semibold
          ${activeTab === "all" ? "bg-red-500" : "border border-white"}`}
        >
          All
        </button>

        <button
          onClick={OnMissedClicked}
          className={`px-12 py-2 rounded-full font-semibold
          ${activeTab === "missed" ? "bg-red-500" : "border border-white"}`}
        >
          Missed
        </button>
      </div>

      {/* ---------------- LIST ---------------- */}
      <div className="flex justify-center">
        <div className="w-full md:w-[50%] bg-[#1f1f1f] rounded-2xl">

          {callHistory.length === 0 && !loading && (
            <div className="py-20 text-center text-gray-400">
              {activeTab === "missed"
                ? "No missed calls to show"
                : "No call history to show"}
            </div>
          )}

          {callHistory.map((item, index) => (
            <div
              key={item.callId}
              onClick={() => OnItemClick(item)}
              className={`flex justify-between items-center px-6 py-5
              ${index !== callHistory.length - 1
                ? "border-b border-gray-700"
                : ""}`}
            >
              {/* Avatar & Info */}
              <div className="flex gap-4">
                <img
                  src={
                    item.type === "outgoing"
                      ? getProfileImage(getMediaUrl(item.receiver.ppImageURL))
                      : getProfileImage(getMediaUrl(item.caller.ppImageURL))
                  }
                  className="w-14 h-14 rounded-full object-cover"
                  alt=""
                />

                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg">
                      {item.type === "outgoing"
                        ? item.receiver.name
                        : item.caller.name}
                    </span>

                    {(item.type === "outgoing"
                      ? item.receiver.is_verified
                      : item.caller.is_verified) && (
                      <MdVerified className="text-blue-400" />
                    )}

                    <span className="text-sm text-gray-400">
                      {formatDate(item.startedAt || item.createdAt)}
                    </span>
                  </div>

                  <p className="text-gray-300 text-sm flex gap-2 items-center mt-1">
                    <FaVideo className="text-gray-500" />
                    {capitalizeFirstLetter(item.type)} ,{" "}
                    {item.durationSeconds === 0
                      ? "No Answer"
                      : formatDuration(item.durationSeconds)}
                  </p>
                </div>
              </div>

              {/* Video Button */}
              <button
                onClick={(e) => OnVideoCallClick(e, item)}
                className="bg-red-500 w-14 h-14 rounded-full flex items-center justify-center"
              >
                <FaVideo className="text-white text-2xl" />
              </button>
            </div>
          ))}

          {/* ---------------- LOADING ---------------- */}
          {loading && (
            <div className="py-6 text-center text-gray-400 text-sm">
              Loading more...
            </div>
          )}
        </div>
      </div>

      {privateCall && (
        <PrivateCallModal
          onClose={() => setPrivateCall(false)}
          host={hostCallData}
        />
      )}
    </div>
  );
}